/**
 * Workshop event catalog — same shape a future API should return.
 *
 * Event: { title, pitch, steps[], complete?, shareFieldIds? }
 * Step: { id, title, timeEstimate?, subtitle?, fields[] }
 * Field types: static | textarea | fill-blank | checkbox-group
 */

export const lettingGoEvent = {
    title: 'The Practice of Letting Go',
    pitch: "You're holding onto something that is holding you back.",
    complete: {
        headlineFieldId: 'release_statement',
        subFieldId: 'becoming',
    },
    shareFieldIds: ['cost', 'served_me', 'release_statement', 'becoming'],
    steps: [
        {
            id: 'regulate',
            title: 'Regulate First',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'static',
                    content: 'Three slow breaths: inhale 4, hold 4, exhale 6, hold 2. Repeat three times.',
                },
                {
                    type: 'textarea',
                    id: 'body_check',
                    prompt: 'What does your body feel right now?',
                },
            ],
        },
        {
            id: 'name_it',
            title: 'Name It',
            timeEstimate: '10 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'freewrite',
                    prompt: 'What have I been carrying that feels heavy, draining, or outdated?',
                },
                {
                    type: 'textarea',
                    id: 'focus',
                    prompt: 'The one thing I want to focus on:',
                },
                {
                    type: 'textarea',
                    id: 'protects_from',
                    prompt: 'I hold onto this because it protects me from:',
                },
                {
                    type: 'textarea',
                    id: 'afraid_if',
                    prompt: "If I let this go, I'm afraid:",
                },
            ],
        },
        {
            id: 'understand_feel',
            title: 'Understand & Feel It',
            timeEstimate: '5 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'describe',
                    prompt: "Describe what's here in your own words:",
                },
                {
                    type: 'textarea',
                    id: 'cost',
                    prompt: 'What has holding onto this cost me in the last year?',
                },
                {
                    type: 'checkbox-group',
                    id: 'emotions',
                    prompt: 'Emotions I notice right now:',
                    options: ['Relief', 'Sadness', 'Fear', 'Gratitude', 'Resistance', 'Grief', 'Shame', 'Other'],
                },
            ],
        },
        {
            id: 'honor_it',
            title: 'Honor It',
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'textarea',
                    id: 'what_it_protected',
                    prompt: 'At some point, this helped me. What was it protecting me from?',
                },
                {
                    type: 'fill-blank',
                    id: 'served_me',
                    prefix: 'I understand that this served me by',
                    suffix: '',
                },
            ],
        },
        {
            id: 'release',
            title: 'Release',
            subtitle: 'The rep that counts',
            fields: [
                {
                    type: 'fill-blank',
                    id: 'release_statement',
                    prefix: 'I am ready to release',
                    suffix: '',
                },
                {
                    type: 'textarea',
                    id: 'body_noticed',
                    prompt: 'What I noticed in my body when I said it aloud:',
                },
            ],
        },
        {
            id: 'step_into_next',
            title: "Step Into What's Next",
            timeEstimate: '3 min',
            fields: [
                {
                    type: 'fill-blank',
                    id: 'becoming',
                    prefix: 'I am someone who',
                    suffix: '',
                },
                {
                    type: 'textarea',
                    id: 'new_practice',
                    prompt: 'One new behavior/practice/belief I will practice this week:',
                },
            ],
        },
    ],
};

export const connectionEvent = {
    title: 'The Practice of Connection',
pitch:
    'Practice three simple skills for stronger relationships: listening so people feel seen, following threads past small talk, and sharing short human stories.',

complete: {
    headlineFieldId: 'connection_commitment',
    subFieldId: 'story_invite',
},

shareFieldIds: ['deeper_question', 'story_invite', 'connection_commitment'],

steps: [
    {
        id: 'arrive',
        title: 'Arrive Together',
        timeEstimate: '2 min',
        fields: [
            {
                type: 'static',
                content:
                    'Take three slow breaths: inhale 4, hold 4, exhale 6, hold 2. Look around the room. For the next few minutes, just sit in the moment and be present.',
            },
        ],
    },

    {
        id: 'listen_belong',
        title: 'Listen for Connection',
        timeEstimate: '5 min',
        fields: [
            {
                type: 'static',
                content:
                    'Skill 1: Listen to understand. Do not try to prepare a response. Let the other person speak without interrupting, fixing, giving advice, or turning the conversation back to yourself. Your job is simply to understand their experience.',
            },
            {
                type: 'static',
                content:
                    'After they finish, reflect back what you heard. You can start with: “What I’m hearing is…” Then ask: “Did I get that right?”',
            },
            {
                type: 'textarea',
                id: 'listen_practice',
                prompt:
                    'What do you think mattered most to them in what they shared?',
            },
        ],
    },

    {
        id: 'go_deeper',
        title: 'Go Beyond Small Talk',
        timeEstimate: '5 min',
        fields: [
            {
                type: 'static',
                content:
                    'Skill 2: Follow the thread. You do not need a perfect “deep question.” Listen for something with energy, emotion, or meaning, and stay with it for one more layer.',
            },
            {
                type: 'static',
                content:
                    'Small talk usually stays with facts. Connection grows when you get curious about what the experience felt like or why it mattered. Try phrases like: “What was that like for you?”, “Why did that matter?”, or “What made that important to you?”',
            },
            {
                type: 'static',
                content:
                    'Go one layer deeper. If the person opens up, stay curious. If they pull back, respect that.',
            },
            {
                type: 'textarea',
                id: 'deeper_question',
                prompt:
                    'What is one thread you could follow deeper in the conversation?',
            },
        ],
    },

    {
        id: 'tell_stories',
        title: 'Tell Stories for Connection',
        timeEstimate: '5 min',
        fields: [
            {
                type: 'static',
                content:
                    'Skill 3: Share something real. A good connection story does not need to be dramatic or deeply personal. It just needs to help someone understand a little more about you.',
            },
            {
                type: 'static',
                content:
                    'Use this simple structure: Moment → Feeling → Meaning. What happened? What did you feel? Why did it matter to you?',
            },
            {
                type: 'static',
                content:
                    'Facts tell people what happened. Stories help people understand what the experience meant to you.',
            },
            {
                type: 'fill-blank',
                id: 'story_invite',
                prefix: 'A small story I could share is about',
                suffix: '',
            },
        ],
    },

    {
        id: 'practice_together',
        title: 'Practice Together',
        timeEstimate: '8 min',
        fields: [
            {
                type: 'static',
                content:
                    'Now combine the three skills in a real conversation. Listen fully. Notice one thread and follow it deeper. When it feels natural, share a short story of your own using Moment → Feeling → Meaning.',
            },
            {
                type: 'static',
                content:
                    'The goal is not to force a deep conversation. The goal is to practice being more present, curious, and real.',
            },
            {
                type: 'checkbox-group',
                id: 'practice_feelings',
                prompt: 'How did the conversation feel?',
                options: [
                    'Seen',
                    'Curious',
                    'Nervous',
                    'Grateful',
                    'Connected',
                    'Surprised',
                    'Other',
                ],
            },
        ],
    },

    {
        id: 'carry_forward',
        title: 'Carry It Forward',
        timeEstimate: '3 min',
        fields: [
            {
                type: 'static',
                content:
                    'Connection gets better with practice. Choose one small thing you want to practice in a conversation this week.',
            },
            {
                type: 'fill-blank',
                id: 'connection_commitment',
                prefix: 'This week, I will practice connection by',
                suffix: '',
            },
        ],
    },
],
}

export const EVENT_CATALOG = {
    'letting-go-sep2026': lettingGoEvent,
    'letting-go-oct2026': lettingGoEvent,
    'connection-oct-2026': connectionEvent,
};

export function normalizeEventCode(raw) {
    return String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
}

export function getEventByCode(rawCode) {
    const eventCode = normalizeEventCode(rawCode);
    if (eventCode && EVENT_CATALOG[eventCode]) {
        return { eventCode, event: EVENT_CATALOG[eventCode], found: true };
    }
    return { eventCode, event: null, found: false };
}

/** Later: fetch from API by code, fallback to EVENT_CATALOG. */
export async function resolveEvent(code) {
    return getEventByCode(code);
}

export const DEFAULT_EVENT_CODE = 'letting-go-oct2026';
